export enum ArticleStatus {
  Active = 'Active',
  Wanted = 'Wanted',
  PendingArrival = 'PendingArrival',
  Sold = 'Sold',
  Disposed = 'Disposed',
}

export interface SystemReference {
  id: string;
  name: string;
  platformOwner: string;
  variants: VariantReference[];
  shortDesc?: string;
  defaultMediaFormat?: string;
}

export interface VariantReference {
  id: string;
  name: string;
  systemId: string;
  system?: SystemReference;
  shortDesc?: string;
  longDesc?: string;
  defaultMediaFormat?: string;
}

export interface GameArticleSystem {
  id: string;
  gameArticleId: string;
  systemId: number;
  variantId?: number;
  mediaFormat?: string;
  isPrimary: boolean;
}

// Form version of GameArticleSystem that uses string IDs for form handling
export interface GameFormSystem {
  id: string;
  gameArticleId: string;
  systemId: string;
  variantId?: string;
  mediaFormat?: string;
  isPrimary: boolean;
}

export interface GameArticleImage {
  id: string;
  gameArticleId: string;
  imageAssetId: string;
  s3Key: string;
  thumbnailUrl?: string;
  cardUrl?: string;
  fullUrl?: string;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface GameDbImage {
  providerId: string;
  providerImageId: string;
  url: string;
  height?: number;
  width?: number;
  type: 'cover' | 'artwork' | 'screenshot';
  lastSync: string;
  gameId: string;
  localizationId?: string | null;
  localization?: {
    region: {
      id: number;
      identifier?: string | null;
    };
  } | null;
}

export interface GameDbGameVideo {
  id: string;
  gameId: string;
  name: string;
  type: string;
  videoReference: string;
}

export interface GameDbPlatform {
  id: string;
  providerId: string;
  providerPlatformId: string;
  systemId?: number | null;
  variantId?: number | null;
  name?: string | null;
  gameArticleId?: string | null;
  gameArticleStatus?: string | null;
}

export interface GameDbCompanyWebsite {
  providerId: string;
  providerWebsiteId: string;
  providerCategoryType: string | number;
  url: string;
  lastSync: string;
  providerCompanyId: string;
}

export interface GameDbCompany {
  id: string;
  providerId: string;
  providerCompanyId: string;
  name: string;
  description?: string;
  startDate?: string;
  url?: string;
  images?: Array<{
    id: string;
    url: string;
    type: string;
  }>;
  games: GameDbCompanyGame[];
  websites?: GameDbCompanyWebsite[];
  lastSync: string;
  providerParentCompanyId?: string | null;
}

export interface GameDbCompanyImage {
  providerId: string;
  providerImageId: string;
  url: string;
  height: number;
  width: number;
  lastSync: string;
  providerCompanyId: string;
}

export interface GameDbCompanyGame {
  id: string;
  name: string;
  gameType?: string;
  role: {
    developer: boolean;
    publisher: boolean;
    porting: boolean;
    supporting: boolean;
  };
  coverImage?: {
    providerId: string;
    providerImageId: string;
    url: string;
    height: number;
    width: number;
    lastSync: string;
    type: string;
    gameId: string;
  } | null;
  firstReleaseDate?: string;
  gameArticleId?: string | null;
  gameArticleStatus?: string | null;
  hasPhysical: 'Yes' | 'No' | 'Unknown';
  gameplayStatus?: 'unplayed' | 'played' | 'basic-completion' | 'full-completion' | null;
  acclaim?: number | null;
}

export interface GameDbGameCompany {
  id: string;
  gameId: string;
  providerId: string;
  providerCompanyId: string;
  developer: boolean;
  porting: boolean;
  publisher: boolean;
  supporting: boolean;
  company: GameDbCompany;
}

export interface GameDbGameAlternativeName {
  id: string;
  gameId: string;
  name: string;
  comment?: string;
}

export interface GameDbGameWebsite {
  providerId: string;
  providerWebsiteId: string;
  providerCategoryType: string;
  url: string;
  gameId: string;
}

export interface GameDbGameGenre {
  genreId: string;
  name: string;
}

export interface GameDbProvider {
  id: string;
  description: string;
  officialUrl: string;
}

export interface GameDbRelease {
  id: string;
  providerId: string;
  providerPlatformId: string;
  releaseDate: string;
  releaseCountry: string;
  systemId: number;
  variantId?: number | null;
  gameArticleId?: string | null;
  gameArticleStatus?: string | null;
  synthetic?: boolean;
  releaseStatusCode?: string | null;
  releaseStatusName?: string | null;
  hasPhysical?: 'Yes' | 'No' | 'Unknown';
}

export interface GameDbAgeRating {
  organisation: string;
  rating: string;
  contentDescriptions: string[];
}

export interface GameDbRelatedGame {
  id: string;
  name: string;
  type: string;
  platforms: string;
  firstReleaseDate: string | null;
  coverImage?: {
    url: string;
  };
}

export interface SteamRating {
  reviewScore: number;
  reviewScoreDesc: string;
  totalPositive: number;
  totalNegative: number;
  lastSync: string;
}

export interface ScreenshotSummary {
  summaryText: string;
  modelUsed: string;
  createdAt: string;
  updatedAt: string;
}

export interface ExtendedPayload {
  screenshotSummary?: ScreenshotSummary;
}

export interface GameDbGame {
  id: string;
  providerId: string;
  gameId: string;
  name: string;
  gameType?: string;
  deleted?: boolean;
  unofficial?: boolean;
  userRating?: number;
  userRatings?: number;
  criticRating?: number;
  criticRatings?: number;
  firstReleaseDate?: string;
  earlyAccessReleaseDate?: string;
  era?: string | null;
  summary?: string;
  storyline?: string;
  versionTitle?: string;
  url?: string;
  lastSync: string;
  images?: GameDbImage[];
  GameDbGameCompany?: GameDbGameCompany[];
  GameDbGameAlternativeName?: GameDbGameAlternativeName[];
  GameDbGameWebsite?: GameDbGameWebsite[];
  GameDbGameGenre?: GameDbGameGenre[];
  provider?: GameDbProvider;
  platforms?: GameDbPlatform[];
  releases?: GameDbRelease[];
  ageRatings?: GameDbAgeRating[];
  videos?: GameDbGameVideo[];
  relatedTo?: GameDbRelatedGame[];
  steamRating?: SteamRating | null;
  classification?: {
    acclaim: number;
    acclaimName?: string;
    acclaimConfidence?: string;
    acclaimRationale?: string | null;
  } | null;
  _extended?: ExtendedPayload;
}

// Type for search results where platforms are returned as string[] (platform IDs only)
export interface GameDbGameSearchResult {
  id: string;
  providerId: string;
  gameId: string;
  name: string;
  gameType?: string;
  deleted?: boolean;
  userRating?: number;
  userRatings?: number;
  criticRating?: number;
  criticRatings?: number;
  firstReleaseDate?: string;
  earlyAccessReleaseDate?: string;
  summary?: string;
  storyline?: string;
  versionTitle?: string;
  url?: string;
  lastSync: string;
  images?: GameDbImage[];
  platforms?: string[]; // Platform IDs (providerPlatformId) in search results
  alternativeNames?: string[];
}

export interface GameArticle {
  id: string;
  userId: string;
  articleType: string;
  articleName: string;
  articleStatus: ArticleStatus;
  packaging?: string;
  version?: string;
  releaseCountry?: string | null;
  systems: GameArticleSystem[];
  tags: string[];
  images: GameArticleImage[];
  mediaFormat?: string;
  conditionScore?: number;
  conditionNotes?: string;
  isGraded?: boolean;
  gradingCompany?: string;
  gradingGrade?: string;
  gradingCertificationNumber?: string;
  contents: GameArticleContentItem[];
  dateOrdered?: string;
  dateAcquired: string;
  pricePaid?: number;
  postage?: number;
  currency?: string;
  dateSold?: string;
  soldPrice?: number;
  soldCurrency?: string;
  mappedPricePaid?: number;
  mappedPostage?: number;
  mappedSoldPrice?: number;
  mappedCurrency?: string;
  storage?: string;
  source?: string;
  gameDbGameId?: string | null;
  gameDbGame?: GameDbGame | null;
  gameplay?: {
    basicCompletion?: string | null;
    firstPlayed?: string | null;
    fullCompletion?: string | null;
    lastPlayed?: string | null;
  } | null;
}

export interface GameArticleContentType {
  code: string;
  label: string;
  displayOrder: number;
}

export interface GameArticleContentItem {
  id?: string;
  contentTypeCode: string;
  description?: string;
  order: number;
}

export interface GameArticleFilters {
  search?: string;
  type?: string | null;
  status?: string | null;
  systemId?: string | null;
  variantId?: string | null;
  packaging?: string | null;
  releaseCountry?: string | null;
  storage?: string | null;
  tags?: string[];
  unlinked?: boolean;
  gameplayStatus?: 'unplayed' | 'played' | 'basic-completion' | 'full-completion' | null;
}

export interface GamePlaySession {
  id: string;
  datePlayed: string;
  basicCompletion: boolean;
  fullCompletion: boolean;
  platformId?: string | null;
  platform?: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
  source: 'manual' | 'steam';
  sourcePlayedAt?: string | null;
  minutesPlayed?: number | null;
}

export interface GamePlaySummary {
  id: string;
  userId: string;
  gameDbGameId: string;
  firstPlayed: string | null;
  lastPlayed: string | null;
  basicCompletion: string | null;
  fullCompletion: string | null;
  basicCompletionTimes?: number;
  fullCompletionTimes?: number;
  createdAt: string;
  updatedAt: string;
  playSessions: GamePlaySession[];
  steamMinutes?: number;
  earliestObservedActivity?: string | null;
}

export interface GameDbGameList {
  id: string;
  userId: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  entries?: GameDbGameListEntry[];
  totalEntryCount?: number;
}

export interface GameDbGameListEntry {
  id: string;
  listId: string;
  gameId: string;
  providerPlatformId?: string;
  sortOrder: number;
  addedAt: string;
  game?: GameDbGame;
  platformInfo?: {
    providerPlatformId: string;
    name: string;
    system?: { id: number; name: string };
    variant?: { id: number; name: string };
    releaseDate?: string;
    releaseCountry?: string;
    hasPhysical?: string;
  };
}

// Physical data types
export interface PhysicalDataOpinion {
  id: string;
  gameId: string;
  providerId: string;
  providerPlatformId: string;
  releaseCountry: string;
  hasPhysical: 'Yes' | 'No' | 'Unknown';
  comment?: string;
}

export interface PhysicalDataRequest {
  hasPhysical: 'Yes' | 'No' | 'Unknown';
  comment?: string;
}

export interface PhysicalDataResponse extends PhysicalDataOpinion {
  message?: string;
}

export interface SearchCriteria {
  field: string;
  operator:
    | 'equals'
    | 'not_equals'
    | 'contains'
    | 'not_contains'
    | 'in'
    | 'not_in'
    | 'gt'
    | 'gte'
    | 'lt'
    | 'lte'
    | 'is_null'
    | 'is_not_null'
    | 'some';
  value: any;
}

export interface BooleanCondition {
  type: 'AND' | 'OR' | 'NOT';
  conditions: (SearchCriteria | BooleanCondition)[];
}

export interface SortField {
  field: string;
  direction: 'asc' | 'desc';
}

export interface SearchModel {
  where?: SearchCriteria | BooleanCondition;
  sort?: SortField[];
  currency?: string;
  limit?: number;
  offset?: number;
}

// User Onboarding Types
export type UserState = 'waiting_identity_link' | 'suspended' | 'active' | 'loading';

export interface LinkableIdentity {
  auth0_sub: string;
  provider: string;
  username: string;
  email: string;
}

export interface FeatureAvailability {
  available: boolean;
  quotaExhausted?: boolean | null;
  quotaLimit?: number | null;
  usageCount?: number | null;
  resetDate?: string | null;
}

export interface UserStateResponse {
  state: UserState;
  user?: {
    id: string;
    username: string;
  };
  preferences?: Record<string, string | null>;
  features?: Record<string, FeatureAvailability>;
  marketingOnboardingCompleted?: boolean;
  hasAcceptedCurrentTerms?: boolean;
  accountCreated?: boolean;
}

export type MarketingMembershipStatus = 'not_subscribed' | 'unconfirmed' | 'confirmed' | 'unsubscribed';

export interface MarketingPreference {
  slug: string;
  title: string;
  description: string;
  brand: 'tgp' | 'retrocritic';
  visibility: 'public' | 'private';
  expectedFrequency: string;
  status: MarketingMembershipStatus;
  deliveryBlocked: boolean;
}

export interface MarketingPreferencesResponse {
  preferences: MarketingPreference[];
  syncedAt: string;
  stale: boolean;
}

export interface LinkIdentityResponse {
  success: boolean;
  state: UserState;
  user?: {
    id: string;
    username: string;
  };
  accountCreated?: boolean;
}

// User Preferences Types
export enum PreferenceDataType {
  BOOLEAN = 'BOOLEAN',
  STRING = 'STRING',
  NUMBER = 'NUMBER',
  SELECT = 'SELECT',
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface PreferenceValue {
  key: string;
  title: string;
  description?: string;
  dataType: PreferenceDataType;
  value: string | null;
  selectOptions?: SelectOption[];
  sortOrder: number;
}

export interface SaveUserPreferencesRequest {
  preferences: {
    key: string;
    value: string;
  }[];
}

export interface SaveUserPreferencesResponse {
  message: string;
}
